import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as models from './models'

export interface State {
  /**
   * Lookup of variables definitions that are being edited
   */
  definitions: State.Lookup
  /**
   * Single variable definition that is currently focused for editing
   */
  focused?: State.Focused
}
export namespace State {
  export const INITIAL: Readonly<State> = {
    definitions: {},
  }

  /**
   * Reset state to initial (when editing is finished/canceled)
   */
  export function reset(state: State): State {
    return {
      ...INITIAL,
    }
  }

  /**
   * Lookup of variable definitions
   */
  export type Lookup = Record<models.Definition['id'], models.Definition>
  export namespace Lookup {
    /**
     * Add initial definitions lookup that is being edited. When creating new
     * entity, this lookup will be empty. It can only have initial definitions
     * when updating existing entity.
     *
     * __NOTE__: Converted from list to lookup for editing purposes in order to
     * prevent adding multiple variables with same ids
     */
    export function set(
      state: State,
      definitions: models.Definition.List,
    ): State {
      return {
        ...state,
        definitions: definitions.reduce((lookup, definition) => {
          lookup[definition.id] = definition
          return lookup
        }, {} as Lookup),
      }
    }

    /**
     * Remove definition from lookup
     */
    export function remove(state: State, id: models.Definition['id']): State {
      // Object is deeply sealed so we need to destructure it first in order
      // to delete property
      state = {
        ...state,
        definitions: { ...state.definitions },
      }
      delete state.definitions[id]
      return state
    }
  }

  /**
   * Currently focused variable definition
   */
  export interface Focused {
    /**
     * Needed if existing definition is being updated so that changes can be
     * reversed
     */
    id?: models.Definition['id']
    definition: Partial<models.Definition>
  }
  export namespace Focused {
    /**
     * Focus variable definition as the one that is currently being edited.
     *
     * Pass {@link models.Definition} id as argument when updating existing
     * definition, or don't pass anything if new definition is created
     */
    export function set(state: State, { id }: set.Request): State {
      return {
        ...state,
        focused: {
          definition: id ? state.definitions[id] : {},
          id,
        },
      }
    }
    export namespace set {
      export interface Request {
        id?: models.Definition['id']
      }
    }

    /**
     * Cancel changes that were made for the definition that is being edited
     */
    export function discard(state: State): State {
      return {
        ...state,
        focused: undefined,
      }
    }

    /**
     * Updates the value for the definition that is currently being edited
     */
    export function update(state: State, request: update.Request): State {
      TypeAsserter.assertIsNotNullish(state.focused)
      const updatedDefinition = request(state.focused.definition)
      return {
        ...state,
        focused: {
          ...state.focused,
          definition: updatedDefinition,
        },
      }
    }
    export namespace update {
      /**
       * Callback which takes focused definition as argument and returns updated
       * definition
       */
      export type Request = (
        definition: Partial<models.Definition>,
      ) => Partial<models.Definition>
    }

    /**
     * Save the focused definition from the definitions lookup that was being
     * updated or add it to lookup if new definition is being created
     */
    export function save(state: State): State {
      const focused = state.focused
      if (focused) {
        const { id, definition } = focused

        if (id) {
          // Object is deeply sealed so we need to destructure it first in order
          // to delete property
          state = {
            ...state,
            definitions: { ...state.definitions },
          }
          // If existing definition is being updated, remove it from state based on
          // previous id
          // NOTE: id can also be updated and that is the reason why we
          // need to remove previous one from the state
          delete state.definitions[id]
        }

        // Add new/updated definition to definitions lookup and clear focused
        // data
        if (
          TypeChecker.isNotNullish(definition.id) &&
          TypeChecker.isNotNullish(definition.name) &&
          TypeChecker.isNotNullish(definition.type)
        ) {
          return {
            ...state,
            definitions: {
              ...state.definitions,
              [definition.id]: definition as models.Definition,
            },
            focused: undefined,
          }
        }
      }

      return state
    }
  }
}
